/**
 * Listen-in (ENG-2136): hear a camera's microphone in live view.
 *
 * Gate: the LaunchDarkly flag `showListenIn` AND the camera has audio (the
 * player decides "has audio" — the mint's has_audio, else a received audio
 * track). Players always start muted (autoplay rules + operator expectation);
 * this button is the user gesture that unmutes. While the operator is
 * talking down to the same camera the button is disabled and the player is
 * forced muted (half-duplex), then the previous listen state comes back.
 */
import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

export const LISTEN_IN_FLAG = "showListenIn";
export const LISTEN_IN_LABEL = "Listen in";
export const MUTE_CAMERA_AUDIO_LABEL = "Mute camera audio";
export const MUTED_WHILE_TALKING_MESSAGE = "Muted while you're talking";

export const useListenInEnabled = (): boolean => {
  const { flags } = useFeatureFlag();
  return Boolean(flags?.[LISTEN_IN_FLAG]);
};

interface ListenInButtonProps {
  /** Audio is currently audible (the user's listen state, not forced). */
  listening: boolean;
  /** Talk-down to this camera is held: forced muted, button disabled. */
  talking: boolean;
  onToggle: () => void;
  size?: "small" | "medium";
}

export const ListenInButton: React.FC<ListenInButtonProps> = ({
  listening,
  talking,
  onToggle,
  size = "medium",
}) => {
  const audible = listening && !talking;
  const label = audible ? MUTE_CAMERA_AUDIO_LABEL : LISTEN_IN_LABEL;
  const button = (
    <IconButton
      aria-label={label}
      aria-pressed={audible}
      disabled={talking}
      size={size}
      onClick={(event) => {
        // Wall tiles are clickable (focus a camera); don't also do that.
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      sx={{
        color: "#fff",
        backgroundColor: audible ? "#FF8400" : "rgba(0,0,0,0.55)",
        "&:hover": {
          backgroundColor: audible ? "#E67300" : "rgba(0,0,0,0.75)",
        },
        "&.Mui-disabled": {
          color: "rgba(255,255,255,0.5)",
          backgroundColor: "rgba(0,0,0,0.55)",
        },
      }}
    >
      {audible ? (
        <VolumeUpIcon fontSize={size === "small" ? "small" : "medium"} />
      ) : (
        <VolumeOffIcon fontSize={size === "small" ? "small" : "medium"} />
      )}
    </IconButton>
  );
  return (
    <Tooltip title={talking ? MUTED_WHILE_TALKING_MESSAGE : label}>
      {/* A disabled button fires no pointer events; the span carries the
          tooltip while talk-down holds it disabled. */}
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
};
